import type { TooltipProps, TypographyProps } from '@mui/material';
import { Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { formatPrice } from '@weft/services';
import type { Currency } from '@weft/types';
import { DEFAULT_CURRENCY } from '@weft/types';
import { isNullOrUndefined, isNumber } from '@weft/utils';
import React from 'react';
import { twMerge } from 'tailwind-merge';

type PriceProps = Omit<TypographyProps, 'align'> & {
  children: React.ReactNode;
  currency?: Currency;
  rounded?: boolean;
  align?: 'left' | 'center' | 'right' | 'unset';
  slots?: {
    Tooltip?: {
      props?: Omit<TooltipProps, 'title' | 'children'>;
    };
  }
  prefix?: React.ReactNode;
};

export const Price: React.FC<PriceProps> = ({
  children,
  currency = DEFAULT_CURRENCY,
  align = 'unset',
  rounded = false,
  slots,
  prefix,
  ...props
}) => {
  if (isNumber(children)) {
    const formattedPrice = !rounded ? formatPrice(children, currency) : formatPrice(children, currency, 0, 0);
    const longerFormattedPrice = formatPrice(children, currency, 16);

    return (
      <Tooltip
        title={longerFormattedPrice}
        placement="top"
        arrow
        {...slots?.Tooltip?.props}
        disableHoverListener={(formattedPrice.length >= longerFormattedPrice.length) || rounded}
      >
        <Typography
          fontSize={14}
          {...props}
          sx={{
            textAlign: align,
            ...props?.sx,
          }}
          className={twMerge('truncate max-w-min', isNullOrUndefined(prefix) && 'first-letter:mr-1', props?.className, align === 'right' && 'ml-auto', align === 'center' && 'mx-auto', align === 'left' && 'mr-auto')}
        >
          {prefix}
          {formattedPrice}
          {(formattedPrice.length < longerFormattedPrice.length && !rounded) && '...'}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={children}
      placement="top"
      arrow
      {...slots?.Tooltip?.props}
    >
      <Typography
        {...props}
        className={twMerge('w-[6ch] truncate', props?.className)}
      >
        {children}
      </Typography>
    </Tooltip>
  );
};